// Global Instructions Rule Applied!
import {
  FieldTypeEnum,
  SelectTypeEnum,
  FieldConfig,
  CompanyFieldConfig,
  SelectFieldConfig,
  ContactFieldConfig
} from '../../../core/components/InputManager/types.ts'
import { introducedByHelp, leadHelp, SOURCED_BY_HELP } from '../../../core/utils/teamContactsHelp.ts'
import { AccountRecordFields } from '../../../state_manager/data_models/account_record.ts'
import { ProspectRecordFields } from '../../../state_manager/data_models/prospect_record.ts'
import {
  TABLE_CONTACTS,
  TABLE_CONTACT_ACCOUNTS,
  FIELD_KEY_SEPARATOR,
  FIELD_KEY_PROG,
  FIELD_KEY_ACTION,
  ACTION_TYPE_UPSERT,
  ACTION_TYPE_DELETE,
  RELATIONSHIP_CATEGORY_LEAD
} from '../../../state_manager/globals.ts'
import { ContactsModelFields } from '../../../state_manager/data_models/contacts_model.ts'
import { ContactAccountsModelFields } from '../../../state_manager/data_models/contact_accounts_model.ts'
import { UserEntryFields } from '../../../state_manager/data_models/user_entry.ts'
import { LOOKUP_CATEGORY_INDUSTRY } from '../../../state_manager/constants/lookupCategories.ts'
import type { ReactNode } from 'react'
import type { SelectListEntry } from '../../../state_manager/data_models/select_list_entry.ts'
import type { Currency } from '../../currencies/interfaces.ts'
import type { ProspectContactsByRole } from './prospectContacts.ts'

// Prospect field constants (following deals pattern)
const PROSPECTS_PREFIX = 'prospects__'
const PIPELINE_PROSPECTS_PREFIX = 'pipeline_prospects__'

export const ProspectFields = {
  prospects__id: `${PROSPECTS_PREFIX}id`,
  prospects__investor: `${PROSPECTS_PREFIX}investor`,
  prospects__fund: `${PROSPECTS_PREFIX}fund`,
  prospects__commitment: `${PROSPECTS_PREFIX}commitment`,
  prospects__allocation_available: `${PROSPECTS_PREFIX}allocation_available`,
  prospects__currency: `${PROSPECTS_PREFIX}currency`,
  prospects__notes: `${PROSPECTS_PREFIX}notes`,
  prospects__pipeline_origination: `${PROSPECTS_PREFIX}pipeline_origination`,
  pipeline_prospects__stage: `${PIPELINE_PROSPECTS_PREFIX}stage`,
  prog__is_new_investor: `${FIELD_KEY_PROG}is_new_investor`,
  prog__tasks: `${FIELD_KEY_PROG}tasks`,
  prog__account_interaction_dates: `${FIELD_KEY_PROG}account_interaction_dates`,
  contact_accounts__introduced_by: `${TABLE_CONTACT_ACCOUNTS}${FIELD_KEY_SEPARATOR}introduced_by`,
  contact_accounts__sourced_by: `${TABLE_CONTACT_ACCOUNTS}${FIELD_KEY_SEPARATOR}sourced_by`,
  contact_prospects__lead: `contact_prospects${FIELD_KEY_SEPARATOR}lead`,
  prog__last_meeting: `${FIELD_KEY_PROG}last_meeting`,
  prog__next_meeting: `${FIELD_KEY_PROG}next_meeting`,
  prog__last_email: `${FIELD_KEY_PROG}last_email`,
  prog__first_meeting: `${FIELD_KEY_PROG}first_meeting`,
  pipeline_prospects__stage__label: `${PIPELINE_PROSPECTS_PREFIX}stage__label`
}

/**
 * Checks if a contact is new (not an existing contact)
 */
function isNewContact(contact: any): boolean {
  if (!contact) return false
  return contact.value === 'new' || (contact.label && contact.label.includes('(new)'))
}

/**
 * Parse contact name into first_name and last_name
 */
function parseContactName(name: string): { first_name: string | null; last_name: string } {
  if (!name || typeof name !== 'string') {
    return { first_name: null, last_name: '' }
  }

  // Remove "(new)" suffix if present
  const cleanName = name.replace(/\s*\(new\)\s*$/i, '').trim()

  if (!cleanName) {
    return { first_name: null, last_name: '' }
  }

  // Split by whitespace
  const nameParts = cleanName.split(/\s+/).filter((part) => part.length > 0)

  if (nameParts.length === 0) {
    return { first_name: null, last_name: '' }
  }

  if (nameParts.length === 1) {
    // Only one part - use as last_name
    return { first_name: null, last_name: nameParts[0] }
  }

  // Two or more parts - first part is first_name, rest is last_name
  return {
    first_name: nameParts[0],
    last_name: nameParts.slice(1).join(' ')
  }
}

/**
 * Transforms contacts array to user_entry format for API requests
 */
export function transformContactsToUserEntries(
  currentContacts: any[],
  previousContacts: any[],
  relationshipCategoryId: string,
  isContactProspects: boolean
): any[] {
  const ALIAS_CONTACTS = `${TABLE_CONTACTS}${FIELD_KEY_SEPARATOR}`
  const ALIAS_CONTACT_ACCOUNTS = `${TABLE_CONTACT_ACCOUNTS}${FIELD_KEY_SEPARATOR}`
  const ALIAS_CONTACT_PROSPECTS = `contact_prospects${FIELD_KEY_SEPARATOR}`

  const FIELD_ID = `${ALIAS_CONTACTS}${ContactsModelFields.id}`
  const FIELD_EMAIL = `${ALIAS_CONTACTS}${ContactsModelFields.email}`
  const FIELD_FIRST_NAME = `${ALIAS_CONTACTS}${ContactsModelFields.first_name}`
  const FIELD_LAST_NAME = `${ALIAS_CONTACTS}${ContactsModelFields.last_name}`
  const FIELD_CONTACT_ACCOUNTS_ID = `${ALIAS_CONTACT_ACCOUNTS}${ContactAccountsModelFields.id}`
  const FIELD_CONTACT_PROSPECTS_ID = `${ALIAS_CONTACT_PROSPECTS}id`
  const PROG_CONTACTS_ACTION = `${FIELD_KEY_PROG}${TABLE_CONTACTS}${FIELD_KEY_ACTION}`
  const PROG_ACTION = `${FIELD_KEY_PROG}${FIELD_KEY_ACTION}`

  // Extract contact IDs for comparison
  const extractContactId = (c: any): string | null => {
    if (!c) return null
    if (typeof c === 'object') {
      return c.value || c.id || (typeof c === 'string' ? c : null)
    }
    if (typeof c === 'string') {
      return c
    }
    return null
  }

  const currentContactIds = new Set(
    (currentContacts || [])
      .map(extractContactId)
      .filter((id): id is string => id !== null && id !== undefined && id !== 'new')
  )
  const previousContactIds = new Set(
    (previousContacts || [])
      .map(extractContactId)
      .filter((id): id is string => id !== null && id !== undefined && id !== 'new')
  )

  const userEntries: any[] = []

  // Process current contacts (new and existing)
  ;(currentContacts || []).forEach((contact: any) => {
    const contactValue = contact && typeof contact === 'object' ? contact.value : contact
    const isNew = isNewContact(contact)

    const userEntry: any = {
      [PROG_ACTION]: ACTION_TYPE_UPSERT
    }

    if (isNew) {
      // New contact - create contact and relationship
      const contactId = crypto.randomUUID()
      const nameSource = contact.data?.originalSearch || contact.label || ''
      const { first_name, last_name } = parseContactName(nameSource)

      userEntry[FIELD_ID] = contactId
      userEntry[FIELD_FIRST_NAME] = first_name || null
      userEntry[FIELD_LAST_NAME] = last_name
      userEntry[FIELD_EMAIL] = contact.email || null
      userEntry[PROG_CONTACTS_ACTION] = ACTION_TYPE_UPSERT
    } else {
      // Existing contact - only update relationship
      userEntry[FIELD_ID] = contactValue
      userEntry[PROG_CONTACTS_ACTION] = ''
    }

    // Set relationship ID field - check previous contacts first to avoid duplicates
    // Find matching contact in previousContacts to get existing relationship ID
    const previousContact = (previousContacts || []).find((prevContact: any) => {
      const prevContactId = extractContactId(prevContact)
      return prevContactId === contactValue
    })

    if (isContactProspects) {
      // Try to get relationship ID from: current contact object, previous contact, or generate new
      // Backend returns contact_prospects__id directly on the contact object
      const relationshipId =
        contact.contact_prospects__id ||
        contact.contact_prospects_id ||
        contact.data?.[FIELD_CONTACT_PROSPECTS_ID] ||
        contact.data?.contact_prospects__id ||
        previousContact?.contact_prospects__id ||
        previousContact?.contact_prospects_id ||
        previousContact?.data?.[FIELD_CONTACT_PROSPECTS_ID] ||
        previousContact?.data?.contact_prospects__id ||
        previousContact?.[FIELD_CONTACT_PROSPECTS_ID] ||
        null

      // Only generate new UUID if contact is new or relationship doesn't exist
      // If contact exists in previousContacts, it should have a relationship ID
      if (relationshipId) {
        userEntry[FIELD_CONTACT_PROSPECTS_ID] = relationshipId
      } else if (isNew || !previousContact) {
        // Only generate new UUID for new contacts or contacts not in previous list
        userEntry[FIELD_CONTACT_PROSPECTS_ID] = crypto.randomUUID()
      } else {
        // Contact exists but no relationship ID found - this shouldn't happen, but generate UUID as fallback
        userEntry[FIELD_CONTACT_PROSPECTS_ID] = crypto.randomUUID()
      }
    } else {
      // Same logic for contact_accounts
      // Backend returns contact_accounts__id directly on the contact object
      const relationshipId =
        contact.contact_accounts__id ||
        contact.contact_accounts_id ||
        contact.data?.[UserEntryFields.contact_accounts__id] ||
        contact.data?.contact_accounts__id ||
        previousContact?.contact_accounts__id ||
        previousContact?.contact_accounts_id ||
        previousContact?.data?.[UserEntryFields.contact_accounts__id] ||
        previousContact?.data?.contact_accounts__id ||
        previousContact?.[FIELD_CONTACT_ACCOUNTS_ID] ||
        null

      if (relationshipId) {
        userEntry[FIELD_CONTACT_ACCOUNTS_ID] = relationshipId
      } else if (isNew || !previousContact) {
        userEntry[FIELD_CONTACT_ACCOUNTS_ID] = crypto.randomUUID()
      } else {
        userEntry[FIELD_CONTACT_ACCOUNTS_ID] = crypto.randomUUID()
      }
    }

    userEntries.push(userEntry)
  })

  // Process removed contacts
  previousContactIds.forEach((contactId) => {
    if (!currentContactIds.has(contactId) && contactId !== 'new' && contactId && typeof contactId === 'string') {
      const previousContact = (previousContacts || []).find((c: any) => {
        if (!c) return false
        if (typeof c === 'object') {
          const cId = extractContactId(c)
          return cId === contactId
        }
        return c === contactId
      })

      if (!previousContact) {
        // Can't find previous contact - skip
        return
      }

      let relationshipId: string | null = null

      if (isContactProspects) {
        // Backend returns contact_prospects__id directly on the contact object
        relationshipId =
          previousContact.contact_prospects__id ||
          previousContact.contact_prospects_id ||
          previousContact.data?.[FIELD_CONTACT_PROSPECTS_ID] ||
          previousContact.data?.contact_prospects__id ||
          previousContact[FIELD_CONTACT_PROSPECTS_ID] ||
          null
      } else {
        // Backend returns contact_accounts__id directly on the contact object
        relationshipId =
          previousContact.contact_accounts__id ||
          previousContact.contact_accounts_id ||
          previousContact.data?.[UserEntryFields.contact_accounts__id] ||
          previousContact.data?.contact_accounts__id ||
          previousContact[FIELD_CONTACT_ACCOUNTS_ID] ||
          null
      }

      if (!relationshipId) {
        // If no relationship ID found, we can't delete - skip this entry
        return
      }

      const userEntry: any = {
        [FIELD_ID]: contactId,
        [PROG_CONTACTS_ACTION]: '',
        [PROG_ACTION]: ACTION_TYPE_DELETE
      }

      if (isContactProspects) {
        userEntry[FIELD_CONTACT_PROSPECTS_ID] = relationshipId
      } else {
        userEntry[FIELD_CONTACT_ACCOUNTS_ID] = relationshipId
      }

      userEntries.push(userEntry)
    }
  })

  return userEntries
}

/**
 * The option/lookup resolvers a prospect form hands to the field builders.
 * Every member is optional: the builders fall back to an empty list when one
 * is absent, and getCompanyDetailsById is null where no company hook is wired.
 */
export interface ProspectFormHelpers {
  getCurrencyById?: (id: string) => Currency | null
  getCurrencyOptions?: () => SelectListEntry[]
  getPipelineStageOptions?: () => SelectListEntry[]
  getFundOptions?: () => SelectListEntry[]
  getFundOptionsFooter?: () => ReactNode
  getLookupOptions?: (category: string) => SelectListEntry[]
  getCompanyDetailsById?: CompanyFieldConfig['getCompanyDetailsById'] | null
}

/**
 * Generates field configurations for Prospect Information section
 */
export function getProspectInfoFields(formData: any, helpers: ProspectFormHelpers): FieldConfig[] {
  const { getCurrencyOptions, getPipelineStageOptions } = helpers

  const fields: FieldConfig[] = [
    // New Investor Checkbox
    {
      key: ProspectFields.prog__is_new_investor,
      type: FieldTypeEnum.CHECKBOX,
      label: '',
      value: formData[ProspectFields.prog__is_new_investor] || false,
      checkboxLabel: 'New Investor?',
      editable: true
    }
  ]

  // Investor field - conditional based on new_investor toggle
  if (!formData[ProspectFields.prog__is_new_investor]) {
    fields.push({
      key: ProspectFields.prospects__investor,
      label: 'Investor',
      type: FieldTypeEnum.COMPANY,
      value: formData[ProspectFields.prospects__investor] || '',
      required: true,
      editable: true,
      getCompanyDetailsById: helpers.getCompanyDetailsById,
      valueTransformer: (value: any, currentRecord: any) => {
        // Extract UUID from select option object (can be {value, label, name} or just a string)
        let investorId = ''

        if (value === null || value === undefined) {
          investorId = ''
        } else if (typeof value === 'string') {
          investorId = value
        } else if (typeof value === 'object') {
          // Handle select option object format {value, label, name}
          investorId = value.value || value.id || ''
        }

        // Return both API field name (investor) and Redux field name (prospects__investor)
        return {
          investor: investorId, // API field name - sent to backend
          [ProspectFields.prospects__investor]: investorId // Redux field name - used for state updates
        }
      }
    } as CompanyFieldConfig)
  }

  // Fund
  fields.push({
    key: ProspectFields.prospects__fund,
    label: 'Fund',
    type: FieldTypeEnum.SELECT,
    value: formData[ProspectFields.prospects__fund] || '',
    required: true,
    editable: true,
    selectType: SelectTypeEnum.FILTER,
    options: helpers.getFundOptions ? helpers.getFundOptions() : [],
    // The gated picker must always offer a way to see what it is hiding
    // (fund status, 2026-09-04). Null when nothing is hidden.
    optionsFooter: helpers.getFundOptionsFooter ? helpers.getFundOptionsFooter() : undefined,
    allowClear: true
  } as SelectFieldConfig)

  // Pipeline Stage
  fields.push({
    key: ProspectFields.pipeline_prospects__stage,
    label: 'Pipeline Stage',
    type: FieldTypeEnum.SELECT,
    value: formData[ProspectFields.pipeline_prospects__stage] || '',
    required: true,
    editable: true,
    selectType: SelectTypeEnum.FILTER,
    options: getPipelineStageOptions ? getPipelineStageOptions() : [],
    allowClear: true
  } as SelectFieldConfig)

  // Currency
  fields.push({
    key: ProspectFields.prospects__currency,
    label: 'Currency',
    type: FieldTypeEnum.SELECT,
    value: formData[ProspectFields.prospects__currency] || '',
    required: false,
    editable: true,
    selectType: SelectTypeEnum.FILTER,
    options: getCurrencyOptions ? getCurrencyOptions() : [],
    allowClear: true
  } as SelectFieldConfig)

  // Commitment
  fields.push({
    key: ProspectFields.prospects__commitment,
    label: 'Commitment',
    type: FieldTypeEnum.CURRENCY,
    value: formData[ProspectFields.prospects__commitment] || '',
    required: false,
    editable: true
  })

  // Allocation Available
  fields.push({
    key: ProspectFields.prospects__allocation_available,
    label: 'Allocation Available',
    type: FieldTypeEnum.CURRENCY,
    value: formData[ProspectFields.prospects__allocation_available] || '',
    required: false,
    editable: true
  })

  // Notes
  fields.push({
    key: ProspectFields.prospects__notes,
    label: 'Notes',
    type: FieldTypeEnum.TEXTAREA,
    value: formData[ProspectFields.prospects__notes] || '',
    required: false,
    editable: true,
    rows: 15
  })

  return fields
}

/**
 * Generates field configurations for Overview Prospect Information section (used in ProspectOverviewSection)
 * Similar to getOverviewDealInfoFields but for prospects
 */
export function getOverviewProspectInfoFields(data: any, options: any, isReadOnly = false): FieldConfig[] {
  const { getCurrencyById, getPipelineStageOptions, getFundOptions, getFundOptionsFooter, getCurrencyOptions } = options

  // If accounts__name is available, use it directly with the UUID for the investor field
  // This avoids needing to look up the company from Redux when the name is already in the data
  // CompanySelect component handles object values with {value, label} format
  const investorValue =
    data[AccountRecordFields.accounts__name] && data[ProspectFields.prospects__investor]
      ? {
          value: data[ProspectFields.prospects__investor],
          label: data[AccountRecordFields.accounts__name]
        }
      : data[ProspectFields.prospects__investor] || ''

  const fields: FieldConfig[] = [
    // Investor - use COMPANY field type with UUID, getCompanyDetailsById will resolve to name
    {
      key: ProspectFields.prospects__investor,
      label: 'Investor',
      type: FieldTypeEnum.COMPANY,
      value: investorValue,
      editable: !isReadOnly,
      getCompanyDetailsById: options.getCompanyDetailsById,
      valueTransformer: (value: any, currentRecord: any) => {
        // Extract UUID from select option object (can be {value, label, name} or just a string)
        let investorId = ''

        if (value === null || value === undefined) {
          investorId = ''
        } else if (typeof value === 'string') {
          investorId = value
        } else if (typeof value === 'object') {
          // Handle select option object format {value, label, name}
          investorId = value.value || value.id || ''
        }

        // Return both API field name (investor) and Redux field name (prospects__investor)
        // The API field will be sent to backend, Redux field will be filtered out but used for state updates
        return {
          investor: investorId, // API field name - sent to backend
          [ProspectFields.prospects__investor]: investorId // Redux field name - used for state updates
        }
      }
    } as CompanyFieldConfig,

    // Fund
    {
      key: ProspectFields.prospects__fund,
      label: 'Fund',
      type: FieldTypeEnum.SELECT,
      value: data[ProspectFields.prospects__fund] || data.fund_name || '',
      editable: !isReadOnly,
      selectType: SelectTypeEnum.FILTER,
      options: getFundOptions ? getFundOptions() : [],
      optionsFooter: getFundOptionsFooter ? getFundOptionsFooter() : undefined,
      allowClear: true
    } as SelectFieldConfig,

    // Pipeline Stage
    {
      key: ProspectFields.pipeline_prospects__stage,
      label: 'Pipeline Stage',
      type: FieldTypeEnum.SELECT,
      value: data[ProspectFields.pipeline_prospects__stage] || data.pipeline_stage || '',
      editable: !isReadOnly,
      selectType: SelectTypeEnum.FILTER,
      options: getPipelineStageOptions ? getPipelineStageOptions() : [],
      allowClear: true,
      patchEndpoint: 'pipeline-prospects',
      patchFields: [
        { sourceKey: 'value', targetField: 'stage' },
        { sourceKey: 'stageDate', targetField: 'stage_date' }
      ],
      valueTransformer: (value, currentRecord) => {
        // Include pipeline_prospects__id from data or currentRecord to override patchId
        const pipelineProspectsId =
          (data as any)?.['pipeline_prospects__id'] ||
          currentRecord?.['pipeline_prospects__id'] ||
          (data as any)?.[ProspectFields.pipeline_prospects__stage] ||
          currentRecord?.[ProspectFields.pipeline_prospects__stage] ||
          null
        const stageDate = new Date().toISOString().split('T')[0]
        const result: any = {
          // API fields (mapped by patchFields)
          value: value, // Will be mapped to 'stage'
          stageDate: stageDate, // Will be mapped to 'stage_date'
          // Redux fields (direct mapping)
          [ProspectFields.pipeline_prospects__stage]: value,
          ['pipeline_prospects__stage_date']: stageDate
        }
        // Only include id if we have a valid pipelineProspectsId
        if (pipelineProspectsId) {
          result.id = pipelineProspectsId
        }
        return result
      }
    } as SelectFieldConfig,

    // Currency
    {
      key: ProspectFields.prospects__currency,
      label: 'Currency',
      type: FieldTypeEnum.SELECT,
      value: data[ProspectFields.prospects__currency] || data.currency || '',
      editable: !isReadOnly,
      selectType: SelectTypeEnum.FILTER,
      options: getCurrencyOptions ? getCurrencyOptions() : [],
      allowClear: true
    } as SelectFieldConfig,

    // Commitment
    {
      key: ProspectFields.prospects__commitment,
      label: 'Commitment',
      type: FieldTypeEnum.CURRENCY,
      value: data[ProspectFields.prospects__commitment] || data.commitment || '',
      editable: !isReadOnly,
      currencySymbol: getCurrencyById(data[ProspectFields.prospects__currency] || data.currency)?.symbol || '$',
      min: 0,
      step: 0.01
    },

    // Allocation Available
    {
      key: ProspectFields.prospects__allocation_available,
      label: 'Allocation Available',
      type: FieldTypeEnum.CURRENCY,
      value: data[ProspectFields.prospects__allocation_available] || data.allocation_available || '',
      editable: !isReadOnly,
      currencySymbol: getCurrencyById(data[ProspectFields.prospects__currency] || data.currency)?.symbol || '$',
      min: 0,
      step: 0.01
    },

    // Notes
    {
      key: ProspectFields.prospects__notes,
      label: 'Notes',
      type: FieldTypeEnum.TEXTAREA,
      value: data[ProspectFields.prospects__notes] || data.notes || '',
      editable: !isReadOnly,
      rows: 15
    }
  ]

  return fields
}

/**
 * Generates field configurations for Overview Prospect Team section (used in ProspectOverviewSection)
 * Reuses getProspectTeamFields but ensures it works for overview/read mode
 */
export function getOverviewProspectTeamFields(
  data: any,
  selectedContacts: any,
  investorLookupId: string | null,
  previousContacts?: any
): FieldConfig[] {
  return getProspectTeamFields(data, selectedContacts, investorLookupId)
}

/**
 * Generates field configurations for New Investor Information section
 * Same fields as new company in deals
 */
export function getNewInvestorFields(formData: any, helpers: ProspectFormHelpers): FieldConfig[] {
  const cityValue = formData[AccountRecordFields.accounts__business_city_code] || ''
  const countryValue = formData[AccountRecordFields.accounts__business_country_code]

  return [
    // Company Name (Investor Name)
    {
      key: AccountRecordFields.accounts__name,
      label: 'Investor',
      type: FieldTypeEnum.TEXT,
      value: formData[AccountRecordFields.accounts__name] || '',
      required: true,
      editable: true,
      autoFocus: false
    },

    // Industry
    {
      key: AccountRecordFields.accounts__industry,
      label: 'Industry',
      type: FieldTypeEnum.SELECT,
      value: formData[AccountRecordFields.accounts__industry] || '',
      editable: true,
      selectType: SelectTypeEnum.LOOKUP,
      lookupCategory: LOOKUP_CATEGORY_INDUSTRY,
      allowClear: true,
      autoFocus: false
    },

    // Website
    {
      key: AccountRecordFields.accounts__website,
      label: 'Website',
      type: FieldTypeEnum.URL,
      value: formData[AccountRecordFields.accounts__website] || '',
      editable: true,
      autoFocus: false
    },

    // Domain
    {
      key: AccountRecordFields.accounts__domain,
      label: 'Domain',
      type: FieldTypeEnum.TEXT,
      value: formData[AccountRecordFields.accounts__domain] || '',
      editable: true,
      autoFocus: false
    },

    // Street address 1
    {
      key: AccountRecordFields.accounts__business_address_1,
      label: 'Street address 1',
      type: FieldTypeEnum.TEXT,
      value: formData[AccountRecordFields.accounts__business_address_1] || '',
      editable: true,
      autoFocus: false
    },

    // Street address 2
    {
      key: AccountRecordFields.accounts__business_address_2,
      label: 'Street address 2',
      type: FieldTypeEnum.TEXT,
      value: formData[AccountRecordFields.accounts__business_address_2] || '',
      editable: true,
      autoFocus: false
    },

    // City
    {
      key: AccountRecordFields.accounts__business_city_code,
      label: 'City',
      type: FieldTypeEnum.CITY,
      value: cityValue,
      editable: true,
      country: countryValue,
      autoFocus: false,
      patchFields: [
        { sourceKey: 'cityCode', targetField: 'business_city_code' },
        { sourceKey: 'cityName', targetField: 'business_city_label' },
        { sourceKey: 'countryCode', targetField: 'business_country_code' },
        { sourceKey: 'state', targetField: 'business_stateprovince' }
      ],
      patchEndpoint: 'accounts',
      valueTransformer: (cityObject, currentRecord) => {
        // Handle clearing: when cityObject is empty string/null/undefined, set values to null explicitly
        const isClearing = !cityObject || cityObject === '' || (typeof cityObject === 'object' && !cityObject.value)

        // Extract values from CitySelect object: { label, value, name, countryCode, country, state }
        // Explicitly set to null when clearing (not undefined) so it's included in JSON
        const cityCode = isClearing ? null : (cityObject?.value ?? null)
        const cityLabel = isClearing ? null : cityObject?.name || cityObject?.label || null

        // Only include countryCode when selecting a city (not when clearing)
        // When clearing city, preserve the existing country
        const countryCode = isClearing ? undefined : (cityObject?.data?.countryCode ?? cityObject?.countryCode ?? undefined)
        const state = isClearing ? null : (cityObject?.data?.state ?? cityObject?.state ?? null)

        const result: Record<string, any> = {
          cityCode: cityCode,
          cityName: cityLabel,
          state: state,
          [ProspectRecordFields.accounts__business_city_code__label]: cityLabel,
          [AccountRecordFields.accounts__business_stateprovince]: state
        }

        // Only include countryCode if we're selecting a city (not clearing)
        if (!isClearing && countryCode !== undefined) {
          result.countryCode = countryCode
          result[AccountRecordFields.accounts__business_country_code] = countryCode
        }

        return result
      }
    },

    // State / Province
    {
      key: AccountRecordFields.accounts__business_stateprovince,
      label: 'State / Province',
      type: FieldTypeEnum.TEXT,
      value: formData[AccountRecordFields.accounts__business_stateprovince] || '',
      editable: true,
      autoFocus: false
    },

    // ZIP code
    {
      key: AccountRecordFields.accounts__business_postalcode,
      label: 'ZIP code',
      type: FieldTypeEnum.TEXT,
      value: formData[AccountRecordFields.accounts__business_postalcode] || '',
      editable: true,
      autoFocus: false
    },

    // Country
    {
      key: AccountRecordFields.accounts__business_country_code,
      label: 'Country',
      type: FieldTypeEnum.COUNTRY,
      value: formData[AccountRecordFields.accounts__business_country_code] || '',
      editable: true,
      autoFocus: false,
      patchFields: [
        { sourceKey: 'countryCode', targetField: 'business_country_code' },
        { sourceKey: 'cityCode', targetField: 'business_city_code' },
        { sourceKey: 'state', targetField: 'business_stateprovince' }
      ],
      patchEndpoint: 'accounts',
      valueTransformer: (countryObject) => {
        // Handle clearing: when countryObject is empty string/null/undefined, set countryCode to null explicitly
        const isClearing =
          !countryObject || countryObject === '' || (typeof countryObject === 'object' && !countryObject.value)
        const countryCode = isClearing ? null : (countryObject?.value ?? null)
        const countryLabel = isClearing ? null : (countryObject?.label ?? null)

        return {
          // API fields (mapped by patchFields)
          // Explicitly set to null when clearing (not undefined) so it's included in JSON
          countryCode: countryCode,
          cityCode: null, // Clear city when country changes
          state: null, // Clear state when country changes
          // Redux fields (direct mapping) - include both code and label for state updates
          [AccountRecordFields.accounts__business_country_code]: countryCode,
          [ProspectRecordFields.accounts__business_country_code__label]: countryLabel,
          [AccountRecordFields.accounts__business_city_code]: null,
          [ProspectRecordFields.accounts__business_city_code__label]: null,
          [AccountRecordFields.accounts__business_stateprovince]: null
        }
      }
    },

    // Company Description
    {
      key: AccountRecordFields.accounts__description,
      label: 'Investor Description',
      type: FieldTypeEnum.TEXTAREA,
      value: formData[AccountRecordFields.accounts__description] || '',
      editable: true,
      rows: 15,
      autoFocus: false
    }
  ]
}

/**
 * Generates field configurations for Investor section (Account fields)
 * Used in DashboardTab for displaying investor information
 * @param {Object} data - Current form data or prospect object
 * @param {Object} options - Options object containing getLookupOptions
 * @param {boolean} isReadOnly - Whether fields should be read-only
 * @returns {Array} Array of FieldConfig objects
 */
export function getInvestorFields(data: any, options: any, isReadOnly = false): FieldConfig[] {
  // Get investor account ID
  const investorAccountId = data[ProspectFields.prospects__investor] || data.investor_id || null

  return [
    // Investor Name (Account Name)
    {
      key: AccountRecordFields.accounts__name,
      label: 'Name',
      type: FieldTypeEnum.TEXT,
      value: data[AccountRecordFields.accounts__name] || data.investor_name || '',
      editable: !isReadOnly,
      patchEndpoint: 'accounts',
      hyperlinked: true,
      hyperlinkModule: 'companies',
      getRecordId: (value: any, currentRecord: any) => {
        return (
          investorAccountId ||
          data?.[AccountRecordFields.accounts__id] ||
          currentRecord?.[AccountRecordFields.accounts__id] ||
          currentRecord?.id ||
          null
        )
      },
      valueTransformer: (value, currentRecord) => {
        const accountId =
          investorAccountId ||
          data?.[AccountRecordFields.accounts__id] ||
          currentRecord?.[AccountRecordFields.accounts__id] ||
          currentRecord?.id
        const result: any = {
          name: value,
          [AccountRecordFields.accounts__name]: value
        }
        if (accountId) {
          result.id = accountId
        }
        return result
      }
    },
    // Street address 1
    {
      key: AccountRecordFields.accounts__business_address_1,
      label: 'Street address 1',
      type: FieldTypeEnum.TEXT,
      value: data[AccountRecordFields.accounts__business_address_1] || data.investor_address_1 || '',
      editable: !isReadOnly,
      patchEndpoint: 'accounts',
      valueTransformer: (value, currentRecord) => {
        const accountId =
          investorAccountId ||
          data?.[AccountRecordFields.accounts__id] ||
          currentRecord?.[AccountRecordFields.accounts__id] ||
          currentRecord?.id
        const result: any = {
          business_address_1: value,
          [AccountRecordFields.accounts__business_address_1]: value
        }
        if (accountId) {
          result.id = accountId
        }
        return result
      }
    },
    // Street address 2
    {
      key: AccountRecordFields.accounts__business_address_2,
      label: 'Street address 2',
      type: FieldTypeEnum.TEXT,
      value: data[AccountRecordFields.accounts__business_address_2] || data.investor_address_2 || '',
      editable: !isReadOnly,
      patchEndpoint: 'accounts',
      valueTransformer: (value, currentRecord) => {
        const accountId =
          investorAccountId ||
          data?.[AccountRecordFields.accounts__id] ||
          currentRecord?.[AccountRecordFields.accounts__id] ||
          currentRecord?.id
        const result: any = {
          business_address_2: value,
          [AccountRecordFields.accounts__business_address_2]: value
        }
        if (accountId) {
          result.id = accountId
        }
        return result
      }
    },
    // City
    {
      key: AccountRecordFields.accounts__business_city_label,
      label: 'City',
      type: FieldTypeEnum.CITY,
      value:
        data['accounts__business_city_code__label'] ||
        data[AccountRecordFields.accounts__business_city_code] ||
        data[AccountRecordFields.accounts__business_city_label] ||
        data.investor_city ||
        '',
      editable: !isReadOnly,
      country:
        data[AccountRecordFields.accounts__business_country_code] ||
        data.investor_country_code ||
        data.investor_country,
      patchFields: [
        { sourceKey: 'cityCode', targetField: 'business_city_code' },
        { sourceKey: 'cityName', targetField: 'business_city_label' },
        { sourceKey: 'countryCode', targetField: 'business_country_code' },
        { sourceKey: 'state', targetField: 'business_stateprovince' }
      ],
      patchEndpoint: 'accounts',
      valueTransformer: (cityObject, currentRecord) => {
        const isClearing = !cityObject || cityObject === '' || (typeof cityObject === 'object' && !cityObject.value)

        const cityCode = isClearing ? null : (cityObject?.value ?? null)
        const cityLabel = isClearing ? null : cityObject?.name || cityObject?.label || null
        const countryCode = isClearing ? undefined : (cityObject?.data?.countryCode ?? undefined)
        const state = isClearing ? null : (cityObject?.data?.state ?? null) || null

        const accountId =
          investorAccountId ||
          data?.[AccountRecordFields.accounts__id] ||
          currentRecord?.[AccountRecordFields.accounts__id] ||
          currentRecord?.id

        const result: any = {
          cityCode,
          cityName: cityLabel,
          state,
          [AccountRecordFields.accounts__business_city_code]: cityCode,
          [AccountRecordFields.accounts__business_city_label]: cityLabel,
          ['accounts__business_city_code__label']: cityLabel,
          [AccountRecordFields.accounts__business_stateprovince]: state
        }

        if (!isClearing && countryCode !== undefined) {
          result.countryCode = countryCode
          result[AccountRecordFields.accounts__business_country_code] = countryCode
        }

        if (accountId) {
          result.id = accountId
        }
        return result
      }
    },
    // State/Province
    {
      key: AccountRecordFields.accounts__business_stateprovince,
      label: 'State',
      type: FieldTypeEnum.TEXT,
      value: data[AccountRecordFields.accounts__business_stateprovince] || data.investor_state || '',
      editable: !isReadOnly,
      patchEndpoint: 'accounts',
      valueTransformer: (value, currentRecord) => {
        const accountId =
          investorAccountId ||
          data?.[AccountRecordFields.accounts__id] ||
          currentRecord?.[AccountRecordFields.accounts__id] ||
          currentRecord?.id
        const result: any = {
          business_stateprovince: value,
          [AccountRecordFields.accounts__business_stateprovince]: value
        }
        if (accountId) {
          result.id = accountId
        }
        return result
      }
    },
    // ZIP code
    {
      key: AccountRecordFields.accounts__business_postalcode,
      label: 'ZIP code',
      type: FieldTypeEnum.TEXT,
      value: data[AccountRecordFields.accounts__business_postalcode] || data.investor_postalcode || '',
      editable: !isReadOnly,
      patchEndpoint: 'accounts',
      valueTransformer: (value, currentRecord) => {
        const accountId =
          investorAccountId ||
          data?.[AccountRecordFields.accounts__id] ||
          currentRecord?.[AccountRecordFields.accounts__id] ||
          currentRecord?.id
        const result: any = {
          business_postalcode: value,
          [AccountRecordFields.accounts__business_postalcode]: value
        }
        if (accountId) {
          result.id = accountId
        }
        return result
      }
    },
    // Country
    {
      key: AccountRecordFields.accounts__business_country_label,
      label: 'Country',
      type: FieldTypeEnum.COUNTRY,
      value:
        data['accounts__business_country_code__label'] ||
        data[AccountRecordFields.accounts__business_country_code] ||
        data[AccountRecordFields.accounts__business_country_label] ||
        data.investor_country ||
        '',
      editable: !isReadOnly,
      patchFields: [
        { sourceKey: 'countryCode', targetField: 'business_country_code' },
        { sourceKey: 'cityCode', targetField: 'business_city_code' },
        { sourceKey: 'state', targetField: 'business_stateprovince' }
      ],
      patchEndpoint: 'accounts',
      valueTransformer: (countryObject, currentRecord) => {
        const isClearing =
          !countryObject || countryObject === '' || (typeof countryObject === 'object' && !countryObject.value)
        const countryCode = isClearing ? null : (countryObject?.value ?? null)
        const countryLabel = isClearing ? null : (countryObject?.label ?? null)

        const accountId =
          investorAccountId ||
          data?.[AccountRecordFields.accounts__id] ||
          currentRecord?.[AccountRecordFields.accounts__id] ||
          currentRecord?.id

        const result: any = {
          countryCode,
          cityCode: null,
          state: null,
          [AccountRecordFields.accounts__business_country_code]: countryCode,
          [AccountRecordFields.accounts__business_country_label]: countryLabel,
          ['accounts__business_country_code__label']: countryLabel,
          [AccountRecordFields.accounts__business_city_code]: null,
          [AccountRecordFields.accounts__business_city_label]: null,
          ['accounts__business_city_code__label']: null,
          [AccountRecordFields.accounts__business_stateprovince]: null
        }
        if (accountId) {
          result.id = accountId
        }
        return result
      }
    },
    // Description
    {
      key: AccountRecordFields.accounts__description,
      label: 'Description',
      type: FieldTypeEnum.TEXTAREA,
      value: data[AccountRecordFields.accounts__description] || data.investor_description || '',
      editable: !isReadOnly,
      rows: 15,
      patchEndpoint: 'accounts',
      valueTransformer: (value, currentRecord) => {
        const accountId =
          investorAccountId ||
          data?.[AccountRecordFields.accounts__id] ||
          currentRecord?.[AccountRecordFields.accounts__id] ||
          currentRecord?.id
        const result: any = {
          description: value,
          [AccountRecordFields.accounts__description]: value
        }
        if (accountId) {
          result.id = accountId
        }
        return result
      }
    },
    // Notes (on accounts table, not prospects)
    {
      key: 'accounts__notes',
      label: 'Notes',
      type: FieldTypeEnum.TEXTAREA,
      value: data['accounts__notes'] || data.notes || '',
      editable: !isReadOnly,
      rows: 15,
      patchEndpoint: 'accounts',
      valueTransformer: (value, currentRecord) => {
        const accountId =
          investorAccountId ||
          data?.[AccountRecordFields.accounts__id] ||
          currentRecord?.[AccountRecordFields.accounts__id] ||
          currentRecord?.id
        const result: any = {
          notes: value,
          accounts__notes: value
        }
        if (accountId) {
          result.id = accountId
        }
        return result
      }
    }
  ]
}

/**
 * Generates field configurations for Prospect Team section (contact relationships).
 * @param includeInteractionDates - When false, omits Last Meeting, Last Email, First Meeting
 *   (used when full form shows them in Interaction Summary section instead)
 */
export function getProspectTeamFields(
  formData: any,
  selectedContacts: any,
  investorLookupId: string | null,
  includeInteractionDates = true
): FieldConfig[] {
  // Ensure all contact arrays are actually arrays before mapping
  const introducedBy = Array.isArray(selectedContacts?.introduced_by)
    ? selectedContacts.introduced_by
    : selectedContacts?.introduced_by
      ? [selectedContacts.introduced_by]
      : []

  const sourcedBy = Array.isArray(selectedContacts?.sourced_by)
    ? selectedContacts.sourced_by
    : selectedContacts?.sourced_by
      ? [selectedContacts.sourced_by]
      : []

  const lead = Array.isArray(selectedContacts?.lead)
    ? selectedContacts.lead
    : selectedContacts?.lead
      ? [selectedContacts.lead]
      : []

  const introducedByOptions = introducedBy.map((c: any) => ({
    value: c.value,
    label: `${c.label}${c.email ? ` (${c.email.toLowerCase()})` : ' (No Email)'}`
  }))

  const sourcedByOptions = sourcedBy.map((c: any) => ({
    value: c.value,
    label: `${c.label}${c.email ? ` (${c.email.toLowerCase()})` : ' (No Email)'}`
  }))

  const leadOptions = lead.map((c: any) => ({
    value: c.value,
    label: `${c.label}${c.email ? ` (${c.email.toLowerCase()})` : ' (No Email)'}`
  }))

  const prevContacts: ProspectContactsByRole = {
    introduced_by: [],
    sourced_by: [],
    lead: []
  }

  return [
    // Introduced By
    {
      key: ProspectFields.contact_accounts__introduced_by,
      label: 'Introduced by',
      labelTooltip: introducedByHelp('investor'),
      type: FieldTypeEnum.CONTACT,
      value: introducedBy,
      editable: true,
      mode: 'multiple',
      options: introducedByOptions
    } as ContactFieldConfig,

    // Sourced By
    {
      key: ProspectFields.contact_accounts__sourced_by,
      label: 'Sourced by',
      labelTooltip: SOURCED_BY_HELP,
      type: FieldTypeEnum.CONTACT,
      value: sourcedBy,
      editable: true,
      mode: 'multiple',
      companyId: investorLookupId,
      options: sourcedByOptions
    } as ContactFieldConfig,

    // Lead
    {
      key: ProspectFields.contact_prospects__lead,
      label: 'Lead',
      labelTooltip: leadHelp('prospect'),
      type: FieldTypeEnum.CONTACT,
      value: lead,
      editable: true,
      mode: 'multiple',
      companyId: investorLookupId,
      options: leadOptions,
      patchEndpoint: 'contact_prospects',
      valueTransformer: (value: any, currentRecord: any) => {
        const currentContacts = Array.isArray(value) ? value : value ? [value] : []
        // Get previous contacts from currentRecord (original value) or fallback to prevContacts
        const previousContacts = currentRecord?.[ProspectFields.contact_prospects__lead] || prevContacts.lead || []
        // Lead uses contact_prospects table
        const userFields = transformContactsToUserEntries(
          currentContacts,
          previousContacts,
          RELATIONSHIP_CATEGORY_LEAD,
          true // isContactProspects = true
        )
        const prospectId =
          formData?.[ProspectFields.prospects__id] || currentRecord?.[ProspectFields.prospects__id] || null
        return {
          id: prospectId,
          userFields,
          relationship_category_id: RELATIONSHIP_CATEGORY_LEAD
        }
      }
    } as ContactFieldConfig,

    ...(includeInteractionDates
      ? ([
          {
            key: ProspectFields.prog__last_meeting,
            label: 'Last Meeting',
            type: FieldTypeEnum.TEXT,
            value:
              formData?.[ProspectFields.prog__account_interaction_dates]?.last_meeting ||
              formData?.prog__account_interaction_dates?.last_meeting ||
              '',
            editable: false,
            patchEndpoint: ''
          },
          {
            key: ProspectFields.prog__next_meeting,
            label: 'Next Meeting',
            type: FieldTypeEnum.TEXT,
            value:
              formData?.[ProspectFields.prog__account_interaction_dates]?.next_meeting ||
              formData?.prog__account_interaction_dates?.next_meeting ||
              formData?.[ProspectFields.prog__account_interaction_dates]?.next_meeting_date ||
              formData?.prog__account_interaction_dates?.next_meeting_date ||
              '',
            editable: false,
            patchEndpoint: ''
          },
          {
            key: ProspectFields.prog__last_email,
            label: 'Last Email',
            type: FieldTypeEnum.TEXT,
            value:
              formData?.[ProspectFields.prog__account_interaction_dates]?.last_email ||
              formData?.prog__account_interaction_dates?.last_email ||
              '',
            editable: false,
            patchEndpoint: ''
          },
          {
            key: ProspectFields.prog__first_meeting,
            label: 'First Meeting',
            type: FieldTypeEnum.TEXT,
            value:
              formData?.[ProspectFields.prog__account_interaction_dates]?.first_meeting ||
              formData?.prog__account_interaction_dates?.first_meeting ||
              '',
            editable: false,
            patchEndpoint: ''
          }
        ] as FieldConfig[])
      : [])
  ]
}

/**
 * Returns read-only Interaction Summary fields (Last Meeting, Last Email, First Meeting)
 * for the full form. Data from prog__account_interaction_dates (view__account_interaction_dates).
 */
export function getInteractionSummaryFields(data: any): FieldConfig[] {
  const interactionDates =
    data?.[ProspectFields.prog__account_interaction_dates] || data?.prog__account_interaction_dates
  return [
    {
      key: ProspectFields.prog__last_meeting,
      label: 'Last Meeting',
      type: FieldTypeEnum.TEXT,
      value: interactionDates?.last_meeting || '',
      editable: false,
      patchEndpoint: ''
    },
    {
      key: ProspectFields.prog__next_meeting,
      label: 'Next Meeting',
      type: FieldTypeEnum.TEXT,
      value: interactionDates?.next_meeting || interactionDates?.next_meeting_date || '',
      editable: false,
      patchEndpoint: ''
    },
    {
      key: ProspectFields.prog__last_email,
      label: 'Last Email',
      type: FieldTypeEnum.TEXT,
      value: interactionDates?.last_email || '',
      editable: false,
      patchEndpoint: ''
    },
    {
      key: ProspectFields.prog__first_meeting,
      label: 'First Meeting',
      type: FieldTypeEnum.TEXT,
      value: interactionDates?.first_meeting || '',
      editable: false,
      patchEndpoint: ''
    }
  ]
}
